/**
 * CRMSidebar — shared vertical left navigation for Admin + Manager CRM dashboards.
 * Replaces the horizontal sales-admin-tabs.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRoleContext, PILL_MAP } from "../hooks/useRoleContext";
import { redirectWithSsoCode } from "../utils/ssoRedirect";

const EBEK_LOGO   = "/logos/ebek-logo.png";
const EBEKMED_LOGO = "/logos/ebekmed-logo.png";

const CTX_ROUTE = { self: "/crm-self", team: "/manager", admin: "/admin", exec: "/executive" };

const APP_URLS = {
  // HRMS base URL — HomeRedirect routes each role to the right dashboard on arrival.
  // Never send to /apps here — that's the workspace picker, not the user's dashboard.
  hrms:    process.env.REACT_APP_HRMS_URL    || "http://localhost:5000",
  expense: process.env.REACT_APP_EXPENSE_URL  || "http://localhost:4000",
  simops:  process.env.REACT_APP_SIMOPS_URL   || "https://simops.nurseng.in",
};

export default function CRMSidebar({ user, tab, setTab, navItems, flaggedCount = 0, logout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { context, setContext, pillDef, allowedApps } = useRoleContext(user, "crm");
  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";
  const logo   = user?.company === "ebekmed" ? EBEKMED_LOGO : EBEK_LOGO;

  const handlePill = (ctx) => { setContext(ctx); navigate(CTX_ROUTE[ctx] || "/dashboard"); };

  const switchApp = async (appKey) => {
    if (appKey === "hrms") {
      // The user already has hrms_token in localStorage from the original HRMS login.
      // Navigate directly to the HRMS app launcher — no SSO code exchange needed.
      // (Using SSO code here would hit HRMS /sso, which on failure redirected to port 7000.)
      window.location.href = APP_URLS.hrms;
      return;
    }
    const t = localStorage.getItem("hrms_token") || localStorage.getItem("crm_token");
    if (!t) return;
    if (appKey === "expense") {
      try {
        await redirectWithSsoCode({ targetUrl: APP_URLS.expense, token: t, hashRoute: true });
      } catch { window.location.href = APP_URLS.expense; }
    }
    if (appKey === "simops") {
      try {
        await redirectWithSsoCode({ targetUrl: APP_URLS.simops, token: t });
      } catch { window.location.href = APP_URLS.simops; }
    }
  };

  return (
    <>
    <button className="crm-mobile-nav-toggle" onClick={() => setMobileOpen(true)} aria-label="Open My Team navigation">☰</button>
    {mobileOpen && <button className="crm-mobile-nav-backdrop" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
    <aside className={`sales-rail crm-role-sidebar${mobileOpen ? " open" : ""}`}>
      {/* Brand */}
      <div className="sales-brand">
        <img className={`sales-brand-logo ${user?.company === "ebekmed" ? "" : "is-ebek-logo"}`} src={logo} alt="logo" onError={e => { e.target.style.display="none"; }} />
        <div className="sales-brand-sub">SALES CRM</div>
      </div>

      <div className="crm-sidebar-identity">
        <div className="crm-sidebar-avatar">
          {user?.name?.[0]?.toUpperCase() || "?"}
          {user?.profile_photo && <img src={user.profile_photo} alt="" />}
        </div>
        <div className="crm-sidebar-identity-copy">
          <strong title={user?.name}>{user?.name || "Employee"}</strong>
          <span>{user?.designation || user?.role || "Employee"} · {user?.company === "ebekmed" ? "Ebek Med" : "Ebek"}</span>
        </div>
      </div>

      {/* Context pills */}
      {pillDef.labels.length > 0 && (
        <div style={{ padding: "0 4px 12px" }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6, paddingLeft: 4 }}>Viewing as</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {pillDef.labels.map((label, i) => {
              const ctx = pillDef.ctxs[i];
              const active = context === ctx;
              return (
                <button key={ctx} onClick={() => handlePill(ctx)}
                  style={{
                    padding: "8px 12px", border: "1px solid",
                    borderColor: active ? "transparent" : "rgba(255,255,255,0.15)",
                    borderRadius: 12,
                    background: active ? `linear-gradient(135deg, ${accent}, ${accent}cc)` : "rgba(255,255,255,0.06)",
                    color: active ? "#fff" : "rgba(255,255,255,0.65)",
                    fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                    cursor: "pointer", textAlign: "left",
                    boxShadow: active ? `0 4px 12px ${accent}44` : "none",
                  }}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Nav items */}
      <nav className="sales-rail-nav">
        {navItems.map(([k, icon, label]) => (
          <button key={k}
            className={`sales-nav-btn ${tab === k ? "active" : ""}`}
            onClick={() => {
              if (k === "productivity") { setMobileOpen(false); navigate("/productivity"); return; }
              if (k === "self") { handlePill("self"); return; }
              setTab(k);
              setMobileOpen(false);
            }}>
            <span className="sales-nav-icon" style={{ fontSize: 11, fontWeight: 900 }}>{icon}</span>
            {label}
            {k === "flagged" && flaggedCount > 0 && (
              <span style={{ marginLeft: "auto", background: "#ef4444", color: "#fff", fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 10, minWidth: 16, textAlign: "center" }}>
                {flaggedCount}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* App switcher + sign out */}
      <div style={{ padding: "10px 4px 4px", borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: "auto" }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, paddingLeft: 4 }}>Switch App</div>
        <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
          {[{ key: "crm", icon: "📊", label: "CRM" }, { key: "hrms", icon: "👤", label: "HRMS" }, { key: "expense", icon: "💰", label: "Expense" }, { key: "simops", icon: "🎓", label: "SimOps" }]
            .filter(a => allowedApps.includes(a.key))
            .map(app => (
              <button key={app.key}
                onClick={() => app.key !== "crm" && switchApp(app.key)}
                style={{
                  flex: 1, padding: "5px 2px",
                  border: "1.5px solid",
                  borderColor: app.key === "crm" ? accent : "rgba(255,255,255,0.2)",
                  borderRadius: 7,
                  background: app.key === "crm" ? `${accent}22` : "rgba(255,255,255,0.06)",
                  color: app.key === "crm" ? accent : "rgba(255,255,255,0.7)",
                  fontSize: 9, fontWeight: 800, fontFamily: "inherit",
                  cursor: app.key === "crm" ? "default" : "pointer",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                }}>
                <span style={{ fontSize: 13 }}>{app.icon}</span>{app.label}
              </button>
            ))}
        </div>
        <button type="button" className="crm-version-button"
          onClick={() => window.dispatchEvent(new Event("ebek:about-open"))}
          aria-label="Open Ebek One version and what's new">
          <span className="crm-version-dot" />
          <span>Ebek One</span>
          <span>v2026.08</span>
        </button>
        <button onClick={() => { logout(); navigate("/login"); }}
          style={{ width: "100%", padding: "8px", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, color: "#fca5a5", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Sign Out
        </button>
      </div>
    </aside>
    </>
  );
}
